import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Close as CloseIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import useCurrentUserGroup from 'src/hooks/queries/useUserGroups';
import { PORTRAIT_LEGACY, PORTRAIT_LEGACY_PLUS } from 'src/constants/general.constants';

const REWARDS_PROGRAM_HELP_URL =
  'https://help.portraitcare.com/Portrait-Rewards-Program-Overview-FAQs-257c0cbfb2f880db8df1e39c4e969e94';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'block',
    width: '100%',
  },
  banner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    borderRadius: 0,
    backgroundColor: '#e3f2fd',
    borderColor: '#2196f3',
    borderBottom: '1px solid #2196f3',
    '& .MuiAlert-message': {
      flex: 1,
      padding: 0,
      minWidth: 0,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      padding: 0,
      marginLeft: theme.spacing(1),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 0,
  },
  icon: {
    fontSize: '1.5rem',
  },
  learnMoreLink: {
    color: '#1976d2',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

interface PlatformUpgradeBannerProps {
  onDismiss?: () => void;
  showDismiss?: boolean;
}

const PlatformUpgradeBanner: React.FC<PlatformUpgradeBannerProps> = ({
  onDismiss,
  showDismiss = true,
}) => {
  const classes = useStyles();
  const [isVisible, setIsVisible] = useState(true);
  const { data: userGroupData, isLoading } = useCurrentUserGroup(true);

  const isNonLegacy =
    userGroupData?.roleName &&
    userGroupData.roleName !== PORTRAIT_LEGACY &&
    userGroupData.roleName !== PORTRAIT_LEGACY_PLUS;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (isLoading || !isNonLegacy || userGroupData?.migratedAt) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Collapse in={isVisible}>
        <Alert
          severity="info"
          className={classes.banner}
          action={
            showDismiss ? (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            ) : null
          }
        >
          <Box className={classes.content}>
            <Box className={classes.message}>
              <Box>
                <Typography variant="body1" component="div" style={{ fontWeight: 600 }}>
                  Upcoming EHR Migration
                </Typography>
                <Typography variant="body2" component="div">
                  Your platform is being upgraded. In the coming days or weeks, your business and all
                  associated data and records will be migrated to Portrait&apos;s New EHR. This new
                  website will replace the one you currently use and includes added features, such as
                  an integrated{' '}
                  <a
                    href={REWARDS_PROGRAM_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    rewards program
                  </a>
                  . You will receive an email once the migration is complete. You can also visit our{' '}
                  <a
                    href="https://help.portraitcare.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Help Center
                  </a>{' '}
                  to view video tutorials showcasing the new platform.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Alert>
      </Collapse>
    </Box>
  );
};

export default PlatformUpgradeBanner;
